import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import "./admin-product-detail.css";
import { HashLoader } from "react-spinners";
import ToastNotification, {
  showToast,
} from "../../components/toasts/ToastNotification";
import {
  deleteProductForAdmin,
  getProductForAdmin,
} from "../../api/product.api";
import { FaCopy } from "react-icons/fa6";
import { productAttributesStatusText, productTypeText } from "../../utils";
import { FaEdit } from "react-icons/fa";
import { MdDelete } from "react-icons/md";
import ConfirmModal from "../../components/modal/confirm-modal";
import { hasPermission } from "../../utils/auth";
import { useUser } from "../../context/UserContext";

export const AdminProductDetail = () => {
  const { productId } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const navigate = useNavigate();
  const { user } = useUser();

  useEffect(() => {
    document.title = `Sản phẩm: ${productId}`;
    fetchProductData();
  }, [productId]);

  const fetchProductData = async () => {
    setLoading(true);
    try {
      const res = await getProductForAdmin(productId);

      if (res.success) {
        setProduct(res.data.product);
      }
    } catch (error) {
      showToast(error.message, "error");
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async (text) => {
    const permission = await navigator.permissions.query({
      name: "clipboard-write",
    });
    if (permission.state === "denied") {
      console.warn("Clipboard permission denied.");
      return;
    }

    await navigator.clipboard.writeText(text);
    showToast("Đã copy dữ liệu", "success");
  };

  const handleDeleteProduct = async (productId) => {
    setLoading(true);
    setIsModalOpen(false);
    try {
      const res = await deleteProductForAdmin(productId);

      if (res.success) {
        navigate("/super-admin/products");
        showToast("Xóa sản phẩm thành công", "success");
      }
    } catch (error) {
      showToast(error.message, "error");
    } finally {
      setLoading(false);
    }
  };

  const productStatusLabel = product?.product_status === "active" ? "Hoạt động" : "Không hoạt động";
  const getProductStatusBadgeClass = (status) =>
    `admin-product-detail-status-badge ${status === "active" ? "is-active" : "is-inactive"}`;
  const getSoldBadgeClass = (sold) =>
    `admin-product-detail-status-badge ${sold ? "is-sold" : "is-available"}`;
  const getPackageStatusBadgeClass = (status) =>
    `admin-product-detail-status-badge ${status === "available" ? "is-available" : "is-inactive"}`;

  if (loading) {
    return (
      <div className="loader-container">
        <HashLoader color="var(--hash-loader-color)" />
      </div>
    );
  }

  return (
    <section className="admin-product-detail-page">
      <div className="admin-product-detail-shell">
        <header className="admin-product-detail-head">
          <div>
            <p className="admin-product-detail-breadcrumb">
              <Link to="/super-admin/products">Quản trị / Sản phẩm</Link> / Chi tiết
            </p>
            <h1 className="admin-product-detail-title">{product?.product_name}</h1>
          </div>
          <div className="admin-product-detail-actions">
            {hasPermission(user?.role, "edit:product") && (
              <button
                onClick={() => navigate(`/super-admin/products/edit/${productId}`)}
                className="admin-product-detail-btn admin-product-detail-btn-primary"
              >
                <FaEdit />
                Sửa
              </button>
            )}
            {hasPermission(user?.role, "delete:product") && (
              <button
                onClick={() => setIsModalOpen(true)}
                className="admin-product-detail-btn admin-product-detail-btn-danger"
              >
                <MdDelete />
                Xóa
              </button>
            )}
          </div>
        </header>

        <div className="admin-product-detail-layout">
          <section className="admin-product-detail-card admin-product-detail-media">
            <img
              loading="lazy"
              className="admin-product-detail-image"
              src={product?.product_imgs?.[0]}
              alt="Ảnh sản phẩm"
            />
            <button
              type="button"
              className="admin-product-detail-copy"
              onClick={() => copyToClipboard(`${product?.productId}`)}
            >
              <FaCopy />
              Copy ID
            </button>
          </section>

          <section className="admin-product-detail-card">
            <h3 className="admin-product-detail-card-title">Thông tin sản phẩm</h3>
            <div className="admin-product-detail-info-grid">
              <div className="admin-product-detail-info-item">
                <span>Tên sản phẩm</span>
                <strong>{product?.product_name}</strong>
              </div>
              <div className="admin-product-detail-info-item">
                <span>Loại sản phẩm</span>
                <strong>{productTypeText(product?.product_type)}</strong>
              </div>
              <div className="admin-product-detail-info-item">
                <span>Danh mục</span>
                <strong>{product?.product_category}</strong>
              </div>
              <div className="admin-product-detail-info-item">
                <span>Trạng thái</span>
                <strong>
                  <span className={getProductStatusBadgeClass(product?.product_status)}>
                    {productStatusLabel}
                  </span>
                </strong>
              </div>
              <div className="admin-product-detail-info-item">
                <span>Tồn kho</span>
                <strong>
                  {product?.product_type === "topup_package"
                    ? product?.product_attributes?.packages?.length
                    : product?.product_attributes?.account?.length}
                </strong>
              </div>
              <div className="admin-product-detail-info-item">
                <span>Đã bán</span>
                <strong>{product?.product_sold_amount || 0}</strong>
              </div>
              <div className="admin-product-detail-info-item">
                <span>Tổng đánh giá</span>
                <strong>{product?.totalReviews || 0}</strong>
              </div>
              <div className="admin-product-detail-info-item">
                <span>Rating</span>
                <strong>{product?.averageRating || 0}</strong>
              </div>
              {(product?.product_type === "game_account" || product?.product_type === "utility_account") && (
                <div className="admin-product-detail-info-item">
                  <span>Up thông tin thủ công</span>
                  <strong>{product?.isValuable ? "Có" : "Không"}</strong>
                </div>
              )}
            </div>
            <div className="admin-product-detail-description">
              <span>Mô tả</span>
              <p>{product?.product_description || "-"}</p>
            </div>
          </section>
        </div>

        <section className="admin-product-detail-card">
          <h3 className="admin-product-detail-card-title">{productTypeText(product?.product_type)}</h3>
          {product?.product_type === "game_account" && (
            <div className="admin-product-detail-table-wrap">
              <table className="admin-product-detail-table">
                <thead>
                  <tr>
                    <th>STT</th>
                    <th>Tài khoản</th>
                    <th>Mật khẩu</th>
                    <th>Tình trạng</th>
                  </tr>
                </thead>
                <tbody>
                  {!product?.isValuable ? (
                    product?.product_attributes?.account?.map((acc, index) => (
                      <tr key={index}>
                        <td>{index + 1}</td>
                        <td>{acc.username}</td>
                        <td>{acc.password}</td>
                        <td>
                          <span className={getSoldBadgeClass(acc.sold)}>
                            {acc.sold ? "Đã bán" : "Chưa bán"}
                          </span>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={4} className="admin-product-detail-empty">Không có tài khoản</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          {(product?.product_type === "utility_account" || product?.product_type === "gift_card") && (
            <div className="admin-product-detail-table-wrap">
              <table className="admin-product-detail-table">
                <thead>
                  <tr>
                    <th>STT</th>
                    <th>Tài khoản</th>
                    <th>Mật khẩu</th>
                    <th>Trạng thái</th>
                  </tr>
                </thead>
                <tbody>
                  {product?.product_attributes?.account?.map((acc, index) => (
                    <tr key={index}>
                      <td>{index + 1}</td>
                      <td>{acc.username}</td>
                      <td>{acc.password}</td>
                      <td>
                        <span className={getSoldBadgeClass(acc.sold)}>
                          {acc.sold ? "Đã bán" : "Chưa bán"}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {product?.product_type === "topup_package" && (
            <div className="admin-product-detail-table-wrap">
              <table className="admin-product-detail-table">
                <thead>
                  <tr>
                    <th>STT</th>
                    <th>Tên gói nạp</th>
                    <th>Giá</th>
                    <th>Trạng thái</th>
                  </tr>
                </thead>
                <tbody>
                  {product?.product_attributes?.packages?.map((pack, index) => (
                    <tr key={index}>
                      <td>{index + 1}</td>
                      <td>{pack.name}</td>
                      <td>{pack.price.toLocaleString()}</td>
                      <td>
                        <span className={getPackageStatusBadgeClass(pack.status)}>
                          {productAttributesStatusText(pack.status)}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>

      <ToastNotification />
      <ConfirmModal
        isOpen={isModalOpen}
        onConfirm={() => handleDeleteProduct(productId)}
        onClose={() => setIsModalOpen(false)}
        message={"Xác nhận bạn đang xóa một sản phẩm"}
        title={"Xóa sản phẩm"}
      />
    </section>
  );
};
